'use client';

import { useEffect } from 'react';
import { useSSE } from './SseProvider';
import {
  shouldResumeOnBackgroundCompleted,
  appendBackgroundSubagentHitlPart,
  isBackgroundSubagentHitl,
  patchMessagesByToolCallId,
  useBackgroundExecutionStore,
} from '../../store/backgroundExecutionStore';
import { useChatInstancesStore } from '../../store/chatInstancesStore';
import { resumeBackgroundCopilotTurn } from '../../store/resume-background-turn';
import {
  BACKGROUND_EXECUTION_COMPLETED_SSE_EVENT,
  BACKGROUND_EXECUTION_STARTED_SSE_EVENT,
  DataMessageTypeEnum,
  TOOL_CALL_PROGRESS_SSE_EVENT,
  type BackgroundExecutionCompletedPayload,
  type BackgroundExecutionStartedPayload,
  type TerraUIMessage,
  type ToolCallGenericMessage,
  type ToolCallProgressPayload,
} from '@terra/shared-dto-chat-types';

function toToolCallProgressType(
  type: ToolCallProgressPayload['type'],
): ToolCallGenericMessage['type'] {
  switch (type) {
    case 'data-toolCallStart':
      return DataMessageTypeEnum.ToolCallStart;
    case 'data-toolCallComplete':
      return DataMessageTypeEnum.ToolCallComplete;
    case 'data-toolCallError':
      return DataMessageTypeEnum.ToolCallError;
    case 'data-toolCallUpdate':
      return DataMessageTypeEnum.ToolCallUpdate;
  }
}

function toToolCallProgressMessage(
  payload: ToolCallProgressPayload,
): ToolCallGenericMessage {
  return {
    id: payload.id,
    type: toToolCallProgressType(payload.type),
    toolCallId: payload.toolCallId,
    toolName: payload.toolName,
    status: payload.status,
    message: payload.message,
    timestamp: payload.timestamp,
    labelPresent: payload.labelPresent,
    labelPast: payload.labelPast,
    error: payload.error,
    icon: payload.icon,
    input: payload.input,
    output: payload.output,
    timing: payload.timing,
    fileMetadata: payload.fileMetadata,
    progressEvents: payload.progressEvents,
    metadata: payload.metadata,
  };
}

function isSubagentTerminalProgress(payload: ToolCallProgressPayload): boolean {
  const parentToolCallId = payload.metadata?.parentToolCallId;

  return (
    payload.metadata?.backgroundChild === true &&
    typeof parentToolCallId === 'string' &&
    parentToolCallId === payload.toolCallId
  );
}

function subagentTerminalStatus(
  payload: ToolCallProgressPayload,
): 'completed' | 'error' | 'aborted' | undefined {
  if (!isSubagentTerminalProgress(payload)) {
    return undefined;
  }

  if (payload.status === 'aborted') {
    return 'aborted';
  }

  if (payload.type === 'data-toolCallError' || payload.status === 'error') {
    return 'error';
  }

  if (payload.type === 'data-toolCallComplete') {
    return 'completed';
  }

  return undefined;
}

function messagesReferenceToolCall({
  messages,
  toolCallId,
}: {
  messages: TerraUIMessage[];
  toolCallId: string;
}): boolean {
  return messages.some((message) =>
    message.parts?.some((part) => {
      const data: unknown = Object.hasOwn(part, 'data')
        ? Reflect.get(part, 'data')
        : undefined;

      return (
        typeof data === 'object' &&
        data !== null &&
        Reflect.get(data, 'toolCallId') === toolCallId
      );
    }),
  );
}

function applyToolCallProgress(payload: ToolCallProgressPayload): void {
  const progress = toToolCallProgressMessage(payload);
  const pending = useBackgroundExecutionStore
    .getState()
    .getPendingExecution(payload.toolCallId);
  const terminalStatus = subagentTerminalStatus(payload);

  if (terminalStatus && pending?.kind === 'subagent') {
    useBackgroundExecutionStore.getState().markSubagentTerminal({
      toolCallId: payload.toolCallId,
      terminalStatus,
    });
  }

  const confirmation = isBackgroundSubagentHitl(progress.output)
    ? progress.output
    : undefined;
  const ownerChatId =
    pending?.copilotThreadId ??
    confirmation?.backgroundSubagent?.copilotThreadId;
  const { chats, configs } = useChatInstancesStore.getState();
  const chatIds = ownerChatId ? [ownerChatId] : [...chats.keys()];

  for (const chatId of chatIds) {
    const chat = chats.get(chatId);
    const setMessages = configs.get(chatId)?.setMessages;

    if (!chat || !setMessages) {
      continue;
    }

    const next = patchMessagesByToolCallId({
      messages: chat.messages,
      toolCallId: payload.toolCallId,
      progress,
    });
    const ownsConfirmation =
      ownerChatId !== undefined ||
      messagesReferenceToolCall({
        messages: chat.messages,
        toolCallId: payload.toolCallId,
      });
    const withHitl =
      confirmation && ownsConfirmation
        ? appendBackgroundSubagentHitlPart({
            messages: next,
            toolCallId: payload.toolCallId,
            confirmation,
          })
        : next;

    if (withHitl !== chat.messages) {
      setMessages(withHitl);
    }
  }
}

export function BackgroundExecutionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { subscribe } = useSSE();

  useEffect(() => {
    const unsubscribeStarted = subscribe(
      BACKGROUND_EXECUTION_STARTED_SSE_EVENT,
      (data: BackgroundExecutionStartedPayload) => {
        useBackgroundExecutionStore.getState().setPendingExecution({
          copilotThreadId: data.copilotThreadId,
          agentThreadId: data.agentThreadId,
          toolCallId: data.toolCallId,
          applicationId: data.applicationId,
          agentType: data.agentType,
          kind: data.kind ?? 'neo',
          title: data.title,
        });
      },
    );

    const unsubscribeCompleted = subscribe(
      BACKGROUND_EXECUTION_COMPLETED_SSE_EVENT,
      (data: BackgroundExecutionCompletedPayload) => {
        const { copilotThreadId, agentThreadId } = data;
        const store = useBackgroundExecutionStore.getState();
        const kind = data.kind ?? 'neo';

        if (
          shouldResumeOnBackgroundCompleted({
            kind,
            isResumeSuppressed: store.isResumeSuppressed(copilotThreadId),
          })
        ) {
          void resumeBackgroundCopilotTurn(copilotThreadId).catch(
            (error: unknown) => {
              console.warn('Failed to resume copilot background turn', error);
            },
          );
        }

        if (kind === 'subagent') {
          const pending = store.getPendingExecutionByAgentThread(agentThreadId);

          if (pending?.toolCallId && !pending.terminalStatus) {
            applyToolCallProgress({
              id: pending.toolCallId,
              type: 'data-toolCallComplete',
              toolCallId: pending.toolCallId,
              toolName: pending.agentType,
              status: 'completed',
              timestamp: Date.now(),
              message: 'completed',
            });
            store.markSubagentTerminal({
              agentThreadId,
              terminalStatus: 'completed',
            });
          }

          return;
        }

        if (kind === 'copilot-follow-up') {
          store.clearTerminalSubagentsForThread({ copilotThreadId });

          return;
        }

        store.clearPendingExecutionsForAgentThread({ agentThreadId });
      },
    );

    const unsubscribeProgress = subscribe(
      TOOL_CALL_PROGRESS_SSE_EVENT,
      applyToolCallProgress,
    );

    return () => {
      unsubscribeStarted();
      unsubscribeCompleted();
      unsubscribeProgress();
    };
  }, [subscribe]);

  return children;
}
